/**
 * Faz 7 drafting long-tail runners: DIVIDE / MEASURE (7.4), TRACE (7.7)
 * and the OVERKILL + REVERSE modify pair (7.7).
 *
 * OCS parity notes:
 * - DIVIDE/MEASURE walk TRUE arc-length over the entity's tessellated
 *   polyline (16+ samples per corner, risk-note density rule); MEASURE
 *   flips the walk direction when the pick lands near the curve's last
 *   vertex; blocks align to the local tangent via atan2.
 * - REVERSE applies the verbatim bulge reconciliation (open n-2-i, closed
 *   (2n-2-i)%n, per-vertex width swap) and the spline knot mirror.
 * - OVERKILL merges only line/arc/circle linework + polyline-linear
 *   cleanup (the OCS reality), in a fixed-point pair loop.
 */

import { runInteraction, PT, PN, PTxt, PK, requireSelection, entityToPolyline, makeEntity } from './lib';
import { undoableAdd, undoableDelete, undoableModify } from '../entityOps';
import { useAppStore, type Entity } from '../../../state/store';
import { useOverkillStore } from '../../../state/useOverkillStore';
import { useBlockStore } from '../../../state/commandAuxStores';
import { reverseSplineData, type SplineData } from '../splineMath';
import { useCommandInteract } from '../commandInteract';
import type { Pt3 } from '../geometryMath';
import type { RunnerMap } from '../runnerRegistry';
import { notify } from '../../../utils/notify';

// ============================================================================
// Arc-length walking (DIVIDE / MEASURE)
// ============================================================================

/** Cumulative chordal length table of a polyline. */
function cumulativeLengths(pts: Pt3[]): number[] {
    const cum = [0];
    for (let i = 1; i < pts.length; i++) {
        cum.push(cum[i - 1] + Math.hypot(
            pts[i][0] - pts[i - 1][0],
            pts[i][1] - pts[i - 1][1],
            (pts[i][2] ?? 0) - (pts[i - 1][2] ?? 0)
        ));
    }
    return cum;
}

/** Point + local tangent at arc length d along the polyline. */
function pointAtDistance(pts: Pt3[], cum: number[], d: number): { point: Pt3; tangent: Pt3 } {
    const total = cum[cum.length - 1];
    const dd = Math.max(0, Math.min(d, total - 1e-9));
    let i = 1;
    while (i < cum.length - 1 && cum[i] < dd) i++;
    const segLen = cum[i] - cum[i - 1] || 1;
    const t = (dd - cum[i - 1]) / segLen;
    const a = pts[i - 1], b = pts[i];
    return {
        point: [
            a[0] + (b[0] - a[0]) * t,
            a[1] + (b[1] - a[1]) * t,
            (a[2] ?? 0) + ((b[2] ?? 0) - (a[2] ?? 0)) * t,
        ],
        tangent: [b[0] - a[0], b[1] - a[1], (b[2] ?? 0) - (a[2] ?? 0)],
    };
}

/** A curve counts as closed when flagged or when the ring wraps. */
function isClosedRing(e: Entity, poly: Pt3[]): boolean {
    if (e.data?.closed === true) return true;
    if (e.type === 'circle' || e.type === 'ellipse') return true;
    const first = poly[0], last = poly[poly.length - 1];
    return poly.length > 2 && Math.hypot(first[0] - last[0], first[1] - last[1]) < 1e-6;
}

/** Entities the DIVIDE/MEASURE pick accepts (entityToPolyline-backed). */
const DIVISIBLE_TYPES = [
    'line', 'polyline', 'polygon', 'rectangle', 'circle', 'arc', 'ellipse',
    'curve', 'spline', 'nurbs-curve', 'hatch', 'leader',
];

/** Shared flow: pick → optional block+align → amount → place marks. */
function startCurveMarkerCommand(measure: boolean): string {
    runInteraction({
        steps: [
            PK(measure ? 'MEASURE: pick the curve to measure' : 'DIVIDE: pick the curve to divide', DIVISIBLE_TYPES),
            PTxt(measure ? 'MEASURE: block name <points>' : 'DIVIDE: block name <points>'),
            { prompt: 'Align block to the curve? [Yes/No] <No>:', kind: 'text', options: [{ keyword: 'Yes' }, { keyword: 'No' }] },
            PN(measure ? 'MEASURE: length of each segment <1>' : 'DIVIDE: number of segments <4>', measure ? 1 : 4),
        ],
        complete: (_p, values, picks, pickPoints) => {
            const e = useAppStore.getState().entities[picks[0]];
            const poly = e ? entityToPolyline(e) : null;
            if (!e || !poly || poly.length < 2) return `${measure ? 'MEASURE' : 'DIVIDE'}: pick has no curve geometry.`;
            const blockName = String(values[1] ?? '').trim();
            const align = String(values[2] ?? '').trim().toUpperCase() === 'YES';
            const amount = Number(values[3]);
            if (measure && !(amount > 0)) return 'MEASURE: segment length must be positive.';

            // Walk direction: a pick near the LAST vertex reverses the march
            // (OCS start-end choice, MEASURE only; DIVIDE is symmetric).
            let walk = poly.slice();
            if (measure && pickPoints?.[0]) {
                const click = pickPoints[0];
                const first = walk[0], last = walk[walk.length - 1];
                const dFirst = Math.hypot(first[0] - click.x, first[1] - click.y);
                const dLast = Math.hypot(last[0] - click.x, last[1] - click.y);
                if (dLast < dFirst) walk = [...walk].reverse();
            }
            const closed = isClosedRing(e, poly);
            const cum = cumulativeLengths(walk);
            const total = cum[cum.length - 1];
            if (total < 1e-10) return `${measure ? 'MEASURE' : 'DIVIDE'}: curve has no length.`;

            const marks: Entity[] = [];
            const placeMark = (d: number) => {
                const { point, tangent } = pointAtDistance(walk, cum, d);
                if (blockName) {
                    const def = useBlockStore.getState().defs[blockName];
                    if (!def) return;
                    const rot = align ? Math.atan2(tangent[1], tangent[0]) : 0;
                    marks.push(makeEntity('insert', `${blockName}-mark`, {
                        blockName, position: [point[0], point[1], point[2] ?? 0],
                        scale: 1, rotation: rot,
                    }));
                } else {
                    marks.push(makeEntity('point', `Point-${Date.now()}`, {
                        position: [point[0], point[1], point[2] ?? 0],
                    }));
                }
            };

            if (measure) {
                const count = Math.min(32767, Math.floor(total / amount));
                if (count < 1) return 'MEASURE: segment length exceeds the curve length.';
                for (let i = 1; i <= count; i++) placeMark(i * amount);
            } else {
                const n = Math.round(amount);
                if (n < 2 || n > 32767) return 'DIVIDE: segments must be 2..32767.';
                const markCount = closed ? n : n - 1;
                for (let i = 0; i < markCount; i++) placeMark((i * total) / n);
            }

            if (marks.length === 0) {
                return blockName
                    ? `${measure ? 'MEASURE' : 'DIVIDE'}: block "${blockName}" is not defined.`
                    : `${measure ? 'MEASURE' : 'DIVIDE'}: no marks placed.`;
            }
            undoableAdd(marks);
            const kind = blockName ? 'block(s)' : 'point mark(s)';
            return `${measure ? 'MEASURE' : 'DIVIDE'}: ${marks.length} ${kind} placed.`
                + (measure && marks.length < 2 ? ' (note: the curve end is not marked)' : '');
        },
    });
    return measure ? 'MEASURE: pick the curve to measure' : 'DIVIDE: pick the curve to divide';
}

// ============================================================================
// REVERSE (7.7)
// ============================================================================

/**
 * LwPolyline bulge reconciliation (OCS reverse.rs, verbatim): a bulge
 * belongs to the segment STARTING at its vertex, so after reversing, new
 * bulge[i] = -old bulge[n-2-i] (open) / (2n-2-i)%n (closed); widths swap
 * start ↔ end.
 */
function reversePolylineData(d: Record<string, any>): Record<string, any> {
    const vertices = Array.isArray(d.vertices) ? (d.vertices as Pt3[]) : [];
    const n = vertices.length;
    if (n < 2) return d;
    const closed = !!d.closed;
    const next: Record<string, any> = { ...d, vertices: [...vertices].reverse() };
    if (Array.isArray(d.bulges) && (d.bulges as number[]).length > 0) {
        const oldB = d.bulges as number[];
        const newB: number[] = new Array(n).fill(0);
        for (let i = 0; i < n; i++) {
            const j = closed ? (((2 * n - 2 - i) % n) + n) % n : n - 2 - i;
            newB[i] = j >= 0 && j < oldB.length ? -oldB[j] : 0;
        }
        next.bulges = newB;
    }
    // Per-vertex width swap (start_width ↔ end_width per reversed vertex).
    if (Array.isArray(d.startWidths) && Array.isArray(d.endWidths)) {
        const sw = [...(d.startWidths as number[])].reverse();
        const ew = [...(d.endWidths as number[])].reverse();
        next.startWidths = ew;
        next.endWidths = sw;
    }
    return next;
}

// ============================================================================
// OVERKILL (7.7) — line/arc/circle union + polyline-linear cleanup
// ============================================================================

const snap = (v: number, tol: number) => Math.round(v / tol) * tol;

/** Masked attribute key per the ignore bitmask (OCS Settings.ignore). */
function attrKey(e: Entity, ignore: number): string {
    const d = e.data as Record<string, unknown>;
    const parts: string[] = [];
    if (!(ignore & 1)) parts.push(`c:${d.color ?? ''}`);
    if (!(ignore & 2)) parts.push(`l:${e.layer}`);
    if (!(ignore & 4)) parts.push(`lt:${d.linetype ?? ''}`);
    if (!(ignore & 8)) parts.push(`s:${d.linetypeScale ?? ''}`);
    if (!(ignore & 16)) parts.push(`w:${d.lineweight ?? ''}`);
    if (!(ignore & 32)) parts.push(`t:${d.thickness ?? ''}`);
    if (!(ignore & 64)) parts.push(`tr:${d.transparency ?? ''}`);
    return parts.join('|');
}

const T = 1e-7;

/** Canonical geometry key of one entity at the given tolerance. */
function geometryKey(e: Entity, tol: number): string | null {
    const d = e.data as Record<string, any>;
    switch (e.type) {
        case 'line': {
            if (!Array.isArray(d.start) || !Array.isArray(d.end)) return null;
            const a = d.start, b = d.end;
            const [p, q] = [`${snap(a[0], tol)},${snap(a[1], tol)}`, `${snap(b[0], tol)},${snap(b[1], tol)}`]
                .sort();
            return `L|${p}|${q}`;
        }
        case 'circle':
            return `C|${snap(d.center?.[0] ?? 0, tol)},${snap(d.center?.[1] ?? 0, tol)}|${snap(d.radius ?? 0, tol)}`;
        case 'arc': {
            const s = ((d.startAngle ?? 0) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
            const ee = ((d.endAngle ?? 0) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
            return `A|${snap(d.center?.[0] ?? 0, tol)},${snap(d.center?.[1] ?? 0, tol)}|${snap(d.radius ?? 0, tol)}|${s.toFixed(6)}|${ee.toFixed(6)}`;
        }
        case 'polyline': {
            const vs = Array.isArray(d.vertices) ? d.vertices : [];
            if (vs.length < 2) return null;
            const body = vs.map((p: number[]) => `${snap(p[0], tol)},${snap(p[1], tol)}`).join(';');
            const bg = Array.isArray(d.bulges) && d.bulges.some((b: number) => Math.abs(b) > 1e-12)
                ? `b${(d.bulges as number[]).map((b) => b.toFixed(6)).join(',')}` : '';
            return `P|${body}|${closedFlag(d)}|${bg}|${d.width ?? ''}`;
        }
        default:
            return null;
    }
}

function closedFlag(d: Record<string, any>): string {
    const vs = Array.isArray(d.vertices) ? d.vertices : [];
    if (d.closed === true) return 'x';
    if (vs.length > 2) {
        const f = vs[0], l = vs[vs.length - 1];
        if (Math.hypot(f[0] - l[0], f[1] - l[1]) < 1e-6) return 'x';
    }
    return 'o';
}

interface ArcRange { start: number; sweep: number }

function arcSweep(start: number, end: number): number {
    let s = end - start;
    while (s <= 0) s += Math.PI * 2;
    return s;
}

/** Union of two angular ranges on the same circle; null when disjoint. */
function unionArc(a: ArcRange, b: ArcRange): ArcRange | 'full' | null {
    const TWO = Math.PI * 2;
    // Try all offsets of b so the ranges overlap.
    for (const off of [-TWO, 0, TWO]) {
        const bs = b.start + off, be = bs + b.sweep;
        const as = a.start, ae = a.start + a.sweep;
        const lo = Math.min(as, bs), hi = Math.max(ae, be);
        const gap = (bs > ae || be < as) ? Math.min(bs - ae, as - be) : 0;
        if (hi - lo <= a.sweep + b.sweep + 1e-9 && gap <= 1e-9) {
            if (hi - lo >= TWO - 1e-6) return 'full';
            return { start: lo, sweep: hi - lo };
        }
    }
    return null;
}

function removeByIds(ids: string[]): void {
    undoableDelete(ids);
}

// ----------------------------------------------------------------------------

export const draftLongtailRunners: RunnerMap = {
    // ---------------- DIVIDE / MEASURE (7.4) ----------------
    'draw.divide': () => startCurveMarkerCommand(false),
    'draw.measure': () => startCurveMarkerCommand(true),

    // ---------------- REVERSE (7.7) ----------------
    'mod.reverse': () => {
        const list = requireSelection('REVERSE');
        if (!list) return 'REVERSE: select objects first.';
        const reversed: string[] = [];
        const supported = new Set(['line', 'polyline', 'spline']);
        const doomedHint = list.filter((e) => !supported.has(e.type));
        list.forEach((e) => {
            if (e.type === 'line') {
                undoableModify([e.id], (d) => {
                    const start = d.start, end = d.end;
                    if (!Array.isArray(start) || !Array.isArray(end)) return d;
                    return { ...d, start: end, end: start };
                });
                reversed.push(e.id);
            } else if (e.type === 'polyline') {
                undoableModify([e.id], (d) => reversePolylineData(d));
                reversed.push(e.id);
            } else if (e.type === 'spline') {
                undoableModify([e.id], (d) => reverseSplineData(d as SplineData));
                reversed.push(e.id);
            }
            // Unsupported types keep asking? OCS loops the pick; here one
            // pass reports the skips honestly (no data is touched).
        });
        const skipped = doomedHint.length;
        return reversed.length
            ? `REVERSE: ${reversed.length} object(s) reversed`
                + (skipped ? `, ${skipped} unsupported type(s) skipped.` : '.')
            : 'REVERSE: no reversible objects in the selection (line/polyline/spline).';
    },

    // ---------------- TRACE (7.7) ----------------
    'draw.trace': () => {
        runInteraction({
            steps: [
                PN('TRACE: width <1.0>', 1.0),
                {
                    prompt: 'TRACE: specify points (U undoes the last point)',
                    kind: 'point',
                    loop: true,
                    minPoints: 2,
                    options: [{ keyword: 'Undo' }],
                },
            ],
            onOption: (keyword) => {
                if (keyword !== 'UNDO' && keyword !== 'U') return;
                const ci = useCommandInteract.getState();
                if (ci.collected.length > 0) {
                    useCommandInteract.setState({ collected: ci.collected.slice(0, -1) });
                }
            },
            complete: (markPts, values, _picks) => {
                const width = Math.max(0.001, Number(values[0]) || 1);
                const pts = markPts;
                if (pts.length < 2) return 'TRACE: at least two points required.';
                const vertices = pts.map((p) => [p.x, p.y, p.z] as Pt3);
                // OCS trace.rs: a wide-band LwPolyline — constant width and
                // per-vertex start/end widths all equal the entered width.
                undoableAdd([makeEntity('polyline', `Trace-${Date.now()}`, {
                    vertices,
                    closed: false,
                    width,
                    constantWidth: width,
                    startWidths: vertices.map(() => width),
                    endWidths: vertices.map(() => width),
                })]);
                return `Trace created: ${vertices.length} points, width ${width.toFixed(3)}.`;
            },
        });
        return 'TRACE: width — then specify points';
    },

    // ---------------- OVERKILL (7.7) ----------------
    'mod.overkill': () => startOverkillOptions(),
};

// ---------------- OVERKILL option loop ----------------

const OVERKILL_CHIPS = [
    { keyword: 'Done' }, { keyword: 'tOlerance' }, { keyword: 'Ignore' },
    { keyword: 'Optimize' }, { keyword: 'combine Plines' },
    { keyword: 'combine Partial overlap' }, { keyword: 'combine Endtoend' },
    { keyword: 'Associativity' },
];

/** OCS modal-free option step: one pick flow where the chips re-arm with
 *  fresh settings and raw picks accumulate for the merge pass. */
function startOverkillOptions(): void {
    const s = useOverkillStore.getState();
    runInteraction({
        steps: [{
            prompt: `OVERKILL: Tolerance=${s.tolerance} Ignore=${s.ignore} Optimize=${s.optimize ? 'Y' : 'N'}`
                + ' — select objects or [Done/tOlerance/Ignore/Optimize/combine Plines/combine Partial overlap/combine Endtoend/Associativity]:',
            kind: 'pick',
            loop: true,
            minPicks: 1,
            options: OVERKILL_CHIPS,
        }],
        onOption: (keyword) => {
            // Chip/typed keywords arrive uppercased with spaces intact.
            const kw = keyword.replace(/[^A-Z]/g, '').toUpperCase();
            const store = useOverkillStore.getState();
            const ci = useCommandInteract.getState();
            const collectedPicks = ci.picks;
            ci.cancelActive();
            if (kw === 'DONE') {
                if (collectedPicks.length > 0) {
                    notify(runOverkill(collectedPicks), 'info');
                    return;
                }
                startOverkillOptions();
                return;
            }
            if (kw === 'TOLERANCE') {
                runInteraction({
                    steps: [PN('OVERKILL tOlerance: numeric tolerance <0.000001>', store.tolerance)],
                    complete: (_p, values) => {
                        const v = Number(values[0]);
                        useOverkillStore.getState().set({ tolerance: Number.isFinite(v) && v >= 0 ? v : 1e-6 });
                        startOverkillOptions();
                        return 'OVERKILL: tolerance updated.';
                    },
                });
                return;
            }
            if (kw === 'IGNORE') {
                runInteraction({
                    steps: [PN('OVERKILL Ignore: bitmask 1=Color 2=Layer 4=Ltype 8=Ltscale 16=LWeight 32=Thickness 64=Transparency (511=all) <0>', store.ignore)],
                    complete: (_p, values) => {
                        const v = Math.round(Number(values[0]));
                        useOverkillStore.getState().set({ ignore: Number.isFinite(v) ? Math.max(0, Math.min(511, v)) : 0 });
                        startOverkillOptions();
                        return 'OVERKILL: ignore mask updated.';
                    },
                });
                return;
            }
            if (kw === 'OPTIMIZE' || kw === 'COMBINEPLINES') {
                store.set({ optimize: !store.optimize });
                startOverkillOptions();
                return;
            }
            if (kw === 'COMBINEPARTIALOVERLAP') {
                store.set({ overlap: !store.overlap });
                startOverkillOptions();
                return;
            }
            if (kw === 'COMBINEENDTOEND') {
                store.set({ endToEnd: !store.endToEnd });
                startOverkillOptions();
                return;
            }
            if (kw === 'ASSOCIATIVITY') {
                store.set({ associativity: !store.associativity });
                startOverkillOptions();
                return;
            }
        },
        complete: (_p, _v, picks) => runOverkill(picks),
    });
    return;
}

function runOverkill(picks: string[]): string {
    const { tolerance, ignore, optimize, overlap, endToEnd } = useOverkillStore.getState();
    const app = useAppStore.getState();
    const tol = Math.max(1e-9, tolerance);
    // Adaylar: picks minus locked-layer entities (associative dims protected
    // by their non-selection in the pick step).
    const lockedLayers = new Set(
        Object.values(app.layers).filter((l) => l.locked).map((l) => l.name)
    );
    const candidates = picks
        .map((id) => app.entities[id])
        .filter((e): e is Entity => !!e && !lockedLayers.has(e.layer));
    if (candidates.length < 2) return 'OVERKILL: need at least two mergeable objects.';

    // ---- Step 1: polyline linear-chain cleanup (bulge/width vertices skip).
    const mutated = new Map<string, Record<string, any>>();
    if (optimize) {
        candidates.forEach((e) => {
            if (e.type !== 'polyline') return;
            const d = e.data as Record<string, any>;
            const vs = Array.isArray(d.vertices) ? (d.vertices as Pt3[]) : [];
            if (vs.length < 3) return;
            if (Array.isArray(d.bulges) && (d.bulges as number[]).some((b) => Math.abs(b) > 1e-12)) return;
            if (Array.isArray(d.startWidths) && (d.startWidths as number[]).some((w) => w > 0)) return;
            const kept: Pt3[] = [vs[0]];
            for (let i = 1; i < vs.length - 1; i++) {
                const a = kept[kept.length - 1], b = vs[i], c = vs[i + 1];
                const cross = (b[0] - a[0]) * (c[1] - b[1]) - (b[1] - a[1]) * (c[0] - b[0]);
                const dot = (b[0] - a[0]) * (c[0] - b[0]) + (b[1] - a[1]) * (c[1] - b[1]);
                // Collinear AND same direction (no spike fold-back).
                if (Math.abs(cross) < 1e-9 && dot >= 0) continue;
                kept.push(b);
            }
            kept.push(vs[vs.length - 1]);
            if (kept.length !== vs.length) {
                mutated.set(e.id, { ...d, vertices: kept });
            }
        });
    }

    // ---- Step 2: fixed-point pair loop over lines/arcs/circles + copies.
    const alive = new Map<string, Entity>();
    candidates.forEach((e) => alive.set(e.id, e));
    // Apply the polyline cleanup to the working copies.
    mutated.forEach((d, id) => {
        const e = alive.get(id);
        if (e) alive.set(id, { ...e, data: d });
    });

    let deleted = 0;
    let changed = true;
    const toDelete = new Set<string>();

    const attr = (e: Entity) => attrKey(e, ignore);
    const gkey = (e: Entity) => geometryKey(e, tol);

    const sameAttrs = (a: Entity, b: Entity) => attr(a) === attr(b);

    const mergeArc = (keep: Entity, drop: Entity): boolean => {
        // Returns true when `drop` was absorbed into `keep`.
        const dk = keep.data as Record<string, any>;
        const dd = drop.data as Record<string, any>;
        const cs = Math.hypot((dk.center?.[0] ?? 0) - (dd.center?.[0] ?? 0), (dk.center?.[1] ?? 0) - (dd.center?.[1] ?? 0));
        const rs = Math.abs((dk.radius ?? 0) - (dd.radius ?? 0));
        if (cs > tol || rs > tol) return false;
        if (keep.type === 'circle' && drop.type === 'circle') return true; // duplicates
        if (keep.type === 'circle' && drop.type === 'arc') return true;   // circle ⊇ arc
        if (keep.type === 'arc' && drop.type === 'circle') {
            // Upgrade the kept arc to the full circle.
            alive.set(keep.id, { ...keep, type: 'circle', data: { ...dk, startAngle: undefined, endAngle: undefined } });
            return true;
        }
        // arc-arc
        const a: ArcRange = { start: dk.startAngle ?? 0, sweep: arcSweep(dk.startAngle ?? 0, dk.endAngle ?? 0) };
        const b: ArcRange = { start: dd.startAngle ?? 0, sweep: arcSweep(dd.startAngle ?? 0, dd.endAngle ?? 0) };
        if (!overlap && !endToEnd) return false;
        if (overlap) {
            const u = unionArc(a, b);
            if (u === 'full') {
                alive.set(keep.id, { ...keep, type: 'circle', data: { ...dk, startAngle: undefined, endAngle: undefined } });
                return true;
            }
            if (u) {
                alive.set(keep.id, { ...keep, data: { ...dk, startAngle: u.start, endAngle: u.start + u.sweep } });
                return true;
            }
        }
        if (endToEnd) {
            const TWO = Math.PI * 2;
            const ae = ((a.start + a.sweep) % TWO + TWO) % TWO;
            const be = ((b.start + b.sweep) % TWO + TWO) % TWO;
            const near = (x: number, y: number) => Math.abs(x - y) < Math.max(tol, 1e-6) || Math.abs(Math.abs(x - y) - TWO) < Math.max(tol, 1e-6);
            if (near(a.start, be) || near(ae, b.start)) {
                const start = near(a.start, be) ? b.start : a.start;
                const sweep = Math.min(TWO, a.sweep + b.sweep);
                if (sweep >= TWO - 1e-6) {
                    alive.set(keep.id, { ...keep, type: 'circle', data: { ...dk, startAngle: undefined, endAngle: undefined } });
                } else {
                    alive.set(keep.id, { ...keep, data: { ...dk, startAngle: start, endAngle: start + sweep } });
                }
                return true;
            }
        }
        return false;
    };

    const mergeLine = (keep: Entity, drop: Entity): boolean => {
        const dk = keep.data as Record<string, any>;
        const dd = drop.data as Record<string, any>;
        const a1 = dk.start as number[], b1 = dk.end as number[];
        const a2 = dd.start as number[], b2 = dd.end as number[];
        if (!a1 || !b1 || !a2 || !b2) return false;
        const ux = b1[0] - a1[0], uy = b1[1] - a1[1];
        const len1 = Math.hypot(ux, uy);
        if (len1 < 1e-9) return false;
        const nx = ux / len1, ny = uy / len1;
        const vx = b2[0] - a2[0], vy = b2[1] - a2[1];
        const len2 = Math.hypot(vx, vy);
        if (len2 < 1e-9) return false;
        // Collinear: parallel directions AND the offset of the second line
        // from the first supporting line within tolerance.
        const cross = nx * (vy / len2) - ny * (vx / len2);
        const off = nx * (a2[1] - a1[1]) - ny * (a2[0] - a1[0]);
        if (Math.abs(cross) > 1e-6 || Math.abs(off) > Math.max(tol, 1e-6)) return false;
        // Also require identical Z level (2D drafting linework).
        if (Math.abs((a1[2] ?? 0) - (a2[2] ?? 0)) > Math.max(tol, 1e-6)) return false;
        const proj = (p: number[]) => (p[0] - a1[0]) * nx + (p[1] - a1[1]) * ny;
        let lo = Math.min(proj(a1), proj(b1));
        let hi = Math.max(proj(a1), proj(b1));
        const l2 = Math.min(proj(a2), proj(b2));
        const h2 = Math.max(proj(a2), proj(b2));
        const touches = overlap ? (l2 <= hi + tol && h2 >= lo - tol) : (Math.abs(l2 - hi) <= tol || Math.abs(h2 - lo) <= tol);
        if (!touches) return false;
        // Same common/thickness/normal condition rides the attr key.
        if (!sameAttrs(keep, drop)) return false;
        lo = Math.min(lo, l2);
        hi = Math.max(hi, h2);
        alive.set(keep.id, {
            ...keep,
            data: { ...dk, start: [a1[0] + nx * lo, a1[1] + ny * lo, a1[2] ?? 0], end: [a1[0] + nx * hi, a1[1] + ny * hi, b1[2] ?? 0] },
        });
        return true;
    };

    while (changed) {
        changed = false;
        const ids = [...alive.keys()].sort().filter((id) => !toDelete.has(id));
        outer:
        for (let i = 0; i < ids.length; i++) {
            for (let j = i + 1; j < ids.length; j++) {
                const A = alive.get(ids[i])!, B = alive.get(ids[j])!;
                if (toDelete.has(A.id) || toDelete.has(B.id)) continue;
                const ga = gkey(A), gb = gkey(B);
                // 1) exact copies (masked normalized key equality).
                if (ga && ga === gb && sameAttrs(A, B)) {
                    toDelete.add(B.id);
                    deleted++;
                    changed = true;
                    continue outer;
                }
                // 2) circle ⊇ arc (both directions), arc-arc union.
                const circleArcPair = (A.type === 'circle' && B.type === 'arc')
                    || (A.type === 'circle' && B.type === 'circle')
                    || (A.type === 'arc' && B.type === 'circle')
                    || (A.type === 'arc' && B.type === 'arc');
                if (circleArcPair && sameAttrs(A, B) && mergeArc(A, B)) {
                    toDelete.add(B.id);
                    deleted++;
                    changed = true;
                    continue outer;
                }
                // 3) line-line union on the shared supporting line.
                if (A.type === 'line' && B.type === 'line' && mergeLine(A, B)) {
                    toDelete.add(B.id);
                    deleted++;
                    changed = true;
                    continue outer;
                }
            }
        }
    }

    // Polyline-only cleanups: commit the vertex reductions even when nothing merged.
    mutated.forEach((d, id) => {
        if (toDelete.has(id)) return;
        const e = alive.get(id);
        if (e) {
            undoableModify([id], () => structuredClone(d));
        }
    });

    if (toDelete.size > 0) removeByIds([...toDelete]);
    return `OVERKILL: ${deleted} object(s) deleted`
        + (optimize && mutated.size ? `, ${mutated.size} polyline(s) simplified.` : '.');
}
